import React, { useState } from 'react';
import clsx from 'clsx';

import { Checkbox } from '@/components/ui/checkbox';
import { type MetricKey, METRIC_KEYS } from '@/components/analytics/types';

export interface ListItemRowProps {
  /** Icon element (social icon, flag, browser icon, etc.) */
  icon?: React.ReactNode;
  /** Label text (domain, country name, browser name) */
  label: string;

  /** Metric values (multi-column mode) */
  views?: string | number;
  uv?: string | number;
  cv?: string | number;
  cvr?: string | number;
  /** Which metric column is currently active/highlighted */
  activeMetric?: MetricKey;

  /** Single value (simple mode — used when multi-column metrics are not provided) */
  value?: string | number;
  /** Trend change string like "+2.5%" or "-4%" */
  change?: string;
  /** Trend direction: positive (green) or negative (coral) */
  changeTrend?: 'positive' | 'negative';

  /** Width of the background progress bar (0-100) */
  barWidthPercent?: number;
  /** Tailwind bg class for the progress bar (default: bg-neutral-50) */
  barColorClass?: string;
  /** Whether the row supports checkbox selection on hover */
  selectable?: boolean;
  /** Current selection state */
  selected?: boolean;
  /** Selection change callback */
  onSelect?: (selected: boolean) => void;
}

export const ListItemRow: React.FC<ListItemRowProps> = ({
  icon,
  label,
  views,
  uv,
  cv,
  cvr,
  activeMetric,
  value,
  change,
  changeTrend,
  barWidthPercent = 0,
  barColorClass = 'bg-neutral-50',
  selectable = false,
  selected = false,
  onSelect,
}) => {
  const [hovered, setHovered] = useState(false);
  const showCheckbox = selectable && (hovered || selected);
  const hideBar = selectable && (hovered || selected);
  const metrics: Record<MetricKey, string | number> = {
    views: views ?? '-',
    uv: uv ?? '-',
    cv: cv ?? '-',
    cvr: cvr ?? '-',
  };
  const isSimpleMode = value !== undefined;
  const isEmptySimple = value === '-' || value === '' || value === 0;

  return (
    <div
      className="self-stretch px-2"
      onMouseEnter={selectable ? () => setHovered(true) : undefined}
      onMouseLeave={selectable ? () => setHovered(false) : undefined}
      onClick={
        selectable
          ? (e) => {
              e.stopPropagation();
              onSelect?.(!selected);
            }
          : undefined
      }
    >
      <div
        className={clsx(
          'relative flex h-8 items-center self-stretch overflow-hidden rounded-md transition-colors duration-150 ease-out',
          selectable && hovered && !selected && 'bg-indigo-100',
          selected && 'bg-indigo-200/50',
          selectable && 'cursor-pointer',
        )}
      >
        {/* Progress bar */}
        {!hideBar && barWidthPercent > 0 && (
          <div
            className={clsx('absolute top-0 left-0 h-8 rounded-md', barColorClass)}
            style={{ width: `${barWidthPercent}%` }}
          />
        )}

        {/* Content — always above the bar */}
        <div className="relative z-10 flex h-8 flex-1 items-center justify-between pr-1 pl-2.5">
          <div className="flex items-center gap-2">
            <div
              className={clsx(
                'grid transition-[grid-template-columns,opacity] duration-150 ease-out',
                showCheckbox ? 'grid-cols-[1rem] opacity-100' : 'grid-cols-[0fr] opacity-0',
              )}
            >
              <div className="overflow-hidden">
                <Checkbox
                  checked={selected}
                  onCheckedChange={(checked) => onSelect?.(!!checked)}
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0"
                />
              </div>
            </div>
            {icon}
            <span className="text-xs leading-4 font-medium text-neutral-500">{label}</span>
          </div>

          {isSimpleMode ? (
            <div className="flex items-center gap-2">
              <span
                className={clsx(
                  'text-xs leading-4 font-medium',
                  isEmptySimple ? 'text-neutral-300' : 'text-neutral-500',
                )}
              >
                {isEmptySimple ? '-' : value}
              </span>
              {change && (
                <span
                  className={clsx(
                    'text-xs leading-4 font-medium',
                    changeTrend === 'positive' ? 'text-green-500' : 'text-coral-500',
                  )}
                >
                  {change}
                </span>
              )}
            </div>
          ) : (
            <div className="flex w-48 items-center self-stretch">
              {METRIC_KEYS.map((key) => {
                const isActive = key === activeMetric;
                const metricValue = metrics[key];
                const isEmpty = metricValue === '-' || metricValue === '' || metricValue === 0;
                return (
                  <div
                    key={key}
                    className="flex flex-1 items-center self-stretch overflow-hidden py-2"
                  >
                    <span
                      className={clsx(
                        'text-xs leading-4 font-medium',
                        isEmpty
                          ? 'text-neutral-300'
                          : selected
                            ? 'text-indigo-500'
                            : isActive || (selectable && hovered)
                              ? 'text-neutral-500'
                              : 'text-neutral-300',
                      )}
                    >
                      {isEmpty ? '-' : metricValue}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
